import { Close } from "@mui/icons-material"
import Refresh from "@mui/icons-material/Refresh"
import {
  Box,
  CircularProgress,
  Divider,
  Drawer,
  IconButton,
  Stack,
  Typography,
} from "@mui/material"
import { GridPaginationModel, GridSortModel } from "@mui/x-data-grid"
import { FC, useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { Link } from "react-router-dom"
import { Locker } from "shared/locker"
import { SharedAccessPatternSaEnum } from "shared/sharedAccessPatternsData"
import { Tenant } from "shared/tenant"
import { TenantThingTypeMrn } from "shared/tenantData"
import { useAppSelector } from "src/app/hooks"
import { selectMeIsReady } from "src/features/me/meSlice"
import { useGetThingsQuery } from "src/features/things/thingsApi"
import { getLockerDisplayValues } from "src/hooks/useLockerDisplayValues"
import ObjectDisplayTable from "./ObjectDisplayTable"
import SaLayout from "./SaLayout"
import SaOrgForm from "./SaOrgForm"
import { buildSaLockersColumns } from "./saLockersColumns"
import SaLockersToolbar, {
  LockerMetrics,
  SstVersionFilter,
} from "./SaLockersToolbar"
import { Sl2Btn } from "./Sl2Btn"
import Sl2DataGrid from "./Sl2DataGrid"

const PAGE_SIZE = 50
const SEARCH_DEBOUNCE_MS = 300

const cardSx = {
  bgcolor: "white",
  border: "1px solid",
  borderColor: "rgba(0, 0, 0, 0.08)",
  borderRadius: 2,
  boxShadow: "0 1px 2px rgba(35, 66, 133, 0.04)",
}

export const SaLockersPage: FC = () => {
  const { t } = useTranslation()
  const isReady = useAppSelector(selectMeIsReady)

  const [isCreating, setIsCreating] = useState(false)
  const [selectedLockerId, setSelectedLockerId] = useState<string | null>(null)

  // Filter / sort / pagination state (all server-driven).
  const [searchInput, setSearchInput] = useState("")
  const [search, setSearch] = useState("")
  const [version, setVersion] = useState<SstVersionFilter>("all")
  const [tenantId, setTenantId] = useState<string | null>(null)
  const [sortModel, setSortModel] = useState<GridSortModel>([
    { field: "name", sort: "asc" },
  ])
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: PAGE_SIZE,
  })

  // Debounce the search box so we don't fire a request per keystroke.
  useEffect(() => {
    const id = setTimeout(() => setSearch(searchInput.trim()), SEARCH_DEBOUNCE_MS)
    return () => clearTimeout(id)
  }, [searchInput])

  // Any filter/sort change resets to the first page.
  useEffect(() => {
    setPaginationModel((prev) => ({ ...prev, page: 0 }))
  }, [search, version, tenantId, sortModel])

  const listParams = useMemo(() => {
    const sort = sortModel[0]
    return {
      "ap.name": SharedAccessPatternSaEnum.SaSearchLockers,
      "ap.limit": paginationModel.pageSize,
      "ap.offset": paginationModel.page * paginationModel.pageSize,
      ...(search ? { "ap.search": search } : {}),
      ...(version !== "all" ? { "ap.sstApiVersion": version } : {}),
      ...(tenantId ? { "ap.tenantId": tenantId } : {}),
      ...(sort?.field
        ? { "ap.sort.field": sort.field, "ap.sort.dir": sort.sort || "asc" }
        : {}),
    }
  }, [search, version, tenantId, sortModel, paginationModel])

  const q = useGetThingsQuery({ params: listParams }, { skip: !isReady })

  const qMetrics = useGetThingsQuery(
    { params: { "ap.name": SharedAccessPatternSaEnum.SaGetLockersMetrics } },
    { skip: !isReady },
  )

  const qTenants = useGetThingsQuery(
    { params: { "ap.name": SharedAccessPatternSaEnum.SaGetTenants } },
    { skip: !isReady },
  )

  const tenants = useMemo(
    () =>
      ((qTenants.data?.things as Tenant[]) || []).filter(
        (tn) => tn.thingTypeMrn === TenantThingTypeMrn,
      ),
    [qTenants.data],
  )

  const rows = (q.data?.things as Locker[]) || []
  const rowCount = q.data?.meta?.count ?? 0

  const metrics = useMemo<LockerMetrics | undefined>(() => {
    const meta = qMetrics.data?.meta as
      | (LockerMetrics & { count: number })
      | undefined
    if (!meta) return undefined
    return {
      total: meta.total ?? meta.count ?? 0,
      v1: meta.v1 ?? 0,
      v2: meta.v2 ?? 0,
      virtual: meta.virtual ?? 0,
    }
  }, [qMetrics.data])

  const isFiltered = !!search || version !== "all" || !!tenantId

  const columns = useMemo(
    () => buildSaLockersColumns(t, { onSelect: setSelectedLockerId }),
    [t],
  )

  const selectedL = useMemo(
    () => rows.find((r) => r.id === selectedLockerId),
    [rows, selectedLockerId],
  ) as Locker

  const isLoading = q.isLoading || q.isFetching
  const refreshAll = () => {
    q.refetch()
    qMetrics.refetch()
  }

  return (
    <SaLayout selectedMenuItemLabelKey="lockers">
      <Box
        sx={{
          maxWidth: 1600,
          mx: "auto",
          px: { xs: 3, md: 5 },
          py: { xs: 4, md: 5 },
          width: "100%",
        }}
      >
        {/* Header */}
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="flex-end"
          flexWrap="wrap"
          gap={2}
          sx={{ mb: 3 }}
        >
          <Box>
            <Typography
              sx={{
                fontSize: "0.72rem",
                fontWeight: 600,
                letterSpacing: "0.14em",
                textTransform: "uppercase",
                color: "primary.main",
              }}
            >
              {t("adminApp")}
            </Typography>
            <Typography
              variant="h4"
              sx={{
                mt: 0.5,
                fontWeight: 700,
                letterSpacing: "-0.02em",
                color: "text.primary",
                lineHeight: 1.15,
              }}
            >
              {t("lockers")}
            </Typography>
          </Box>
          <Stack direction="row" spacing={1} alignItems="center">
            {isLoading ? (
              <CircularProgress size={22} />
            ) : (
              <IconButton
                onClick={refreshAll}
                size="small"
                sx={{
                  bgcolor: "rgba(35, 66, 133, 0.06)",
                  color: "primary.main",
                  "&:hover": { bgcolor: "rgba(35, 66, 133, 0.12)" },
                }}
              >
                <Refresh fontSize="small" />
              </IconButton>
            )}
            <Link to="/sa/lockers/new">
              <Sl2Btn variant="contained" disableElevation>
                {t("createLocker")}
              </Sl2Btn>
            </Link>
          </Stack>
        </Stack>

        {/* Stats + filters */}
        <SaLockersToolbar
          t={t}
          cardSx={cardSx}
          searchInput={searchInput}
          onSearchChange={setSearchInput}
          version={version}
          onVersionChange={setVersion}
          tenantId={tenantId}
          tenants={tenants}
          onTenantChange={setTenantId}
          metrics={metrics}
          filteredCount={rowCount}
          isFiltered={isFiltered}
        />

        {/* Create form (conditional) */}
        {isCreating && (
          <Box sx={{ ...cardSx, p: { xs: 2, md: 2.5 }, mb: 2.5, maxWidth: 800 }}>
            <SaOrgForm
              isCreating={true}
              onFinish={() => {
                refreshAll()
                setIsCreating(false)
              }}
              onCancel={() => setIsCreating(false)}
            />
          </Box>
        )}

        {/* Data grid */}
        <Box sx={{ ...cardSx, overflow: "hidden" }}>
          <Sl2DataGrid
            serverMode
            datagridProps={{
              columns,
              rows,
              getRowId: (row) => row.id,
              disableColumnMenu: true,
              // Vertically center cell content (LOCKER-323).
              sx: {
                "& .MuiDataGrid-cell": {
                  display: "flex",
                  alignItems: "center",
                },
              },
            }}
            listCount={rowCount}
            rowCount={rowCount}
            isLoading={isLoading}
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            sortModel={sortModel}
            onSortModelChange={setSortModel}
            pageSizeOptions={[25, 50, 100]}
          />
        </Box>
      </Box>

      {selectedLockerId && (
        <Drawer data-ui="UI-SL2W-141"
          anchor="right"
          open={!!selectedLockerId}
          onClose={() => setSelectedLockerId(null)}
          sx={{
            "& .MuiDrawer-paper": {
              width: "80vw",
              maxWidth: "1500px",
              maxHeight: "calc(100vh - 64px)",
              height: "calc(100vh - 64px)",
              paddingTop: "64px",
            },
          }}
        >
          <Stack direction="column" spacing={1} p={2}>
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              justifyContent="space-between"
            >
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                sx={{ color: "primary.main" }}
                divider={<Divider orientation="vertical" flexItem />}
              >
                <Typography variant="body1" fontWeight="bold">
                  {getLockerDisplayValues(selectedL).name}
                </Typography>
                <Typography variant="body1" fontWeight="bold">
                  {selectedL?.id}
                </Typography>
              </Stack>
              <IconButton onClick={() => setSelectedLockerId(null)}>
                <Close />
              </IconButton>
            </Stack>
            <Divider />
            <ObjectDisplayTable data={selectedL} />
          </Stack>
        </Drawer>
      )}
    </SaLayout>
  )
}

export default SaLockersPage
